import React, { useEffect, useCallback } from "react"
import styled from "styled-components"
import { useRouter } from "next/router"
import {
  strategyOptions,
  PaymentMethod,
  NumberOfPayments,
} from "src/features/calculator/utils/calculator"
import { useForm } from "react-hook-form"
import { calculationSchema, CalculationSchema, UrlSchema } from "src/features/calculator/schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { SelectField, Option } from "src/features/calculator/components/field/SelectField"
import { ApplicationInformation } from "src/features/calculator/components/ApplicationInformation"
import { autoDetermineFormula } from "src/features/calculator/utils/autoDetect/autoDetermineFormula"
import dayjs from "dayjs"
import { autoDetect } from "src/features/calculator/utils/autoDetect/service"
import {
  makeSpreadSheetColumnsText,
  toLineText,
} from "src/features/calculator/utils/makeSpreadSheetColumnsText"
import { updateQueryParams } from "src/features/calculator/utils/updateQueryParams"
import { Formulas } from "src/features/calculator/components/formula/Formulas"
import { debounce } from "lodash"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"

type Props = {
  queryParams: UrlSchema
}

const buttonType = {
  SpreadSheet: "SpreadSheet",
  Line: "Line",
} as const

type ButtonType = (typeof buttonType)[keyof typeof buttonType]

function isButtonType(value: string): value is ButtonType {
  return Object.values(buttonType).includes(value as ButtonType)
}

export function Calculator({ queryParams }: Props) {
  const router = useRouter()
  const { meta, calc } = queryParams
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CalculationSchema>({
    resolver: zodResolver(calculationSchema),
    defaultValues: {
      applicationId: meta?.applicationId || "",
      userId: meta?.userId || "",
      userName: meta?.userName || "",
      storeId: meta?.storeId || "",
      storeName: meta?.storeName || "",
      adPrice: meta?.adPrice || 0,
      isRecipient: meta?.isRecipient || "false",
      nop: meta?.nop || NumberOfPayments.Split3,
      paymentMethod: meta?.paymentMethod || PaymentMethod.Undecided,
      scheduledDealAt: meta?.scheduledDealAt || "",
      applicatedAt: meta?.applicatedAt || dayjs().subtract(1, "day").format("YYYY-MM-DD"),
      strategy: meta?.strategy,
      exCustomerTransferFeeRate:
        calc?.customerTransferFeeRate?.params?.exCustomerTransferFeeRate || 0,
      consumptionTaxRate: calc?.customerTransferFeeRate?.params?.consumptionTaxRate || 0,
      maxAdvancePayment: calc?.totalReimbursement?.params.maxAdvancePayment || 0,
      initialCost: calc?.totalReimbursement?.params.initialCost || 0,
      downPayment: calc?.totalReimbursement?.params.downPayment || 0,
      rent: calc?.customerTransferFee?.params.rent || 0,
      customerTransferFeeRate: calc?.customerTransferFeeRate?.result || 0,
      totalReimbursement: calc?.remittance?.params.totalReimbursement || 0,
      customerTransferFee: calc?.remittance?.params.customerTransferFee || 0,
      monthlyTransferCount: calc?.exCustomerTransferFeeRate?.params?.monthlyTransferCount || 0,
      exDiscountedCustomerTransferFeeRate:
        calc?.customerTransferFeeRate?.params?.exDiscountedCustomerTransferFeeRate || 0,
      customerTransferFeeRateResult: calc?.customerTransferFeeRate?.result || 0,
      totalReimbursementResult: calc?.totalReimbursement?.result || 0,
      selfPaymentResult: calc?.selfPayment?.result || 0,
      customerTransferFeeResult: calc?.customerTransferFee?.result || 0,
      remittanceResult: calc?.remittance?.result || 0,
      exCustomerTransferFeeRateResult: calc?.exCustomerTransferFeeRate?.result || 0,
    },
  })

  const watchedValues = watch()
  const { strategy, adPrice, storeName, isRecipient, nop, applicatedAt } = watchedValues

  async function copyText(data: CalculationSchema, event: any) {
    updateQueryParams(watchedValues)
    const button = event.nativeEvent instanceof SubmitEvent ? event.nativeEvent.submitter : null

    const type = button?.getAttribute("value")
    if (!isButtonType(type)) {
      return alert("無効なbutton Typeです")
    }

    let text = ""
    if (type === buttonType.SpreadSheet) {
      text = makeSpreadSheetColumnsText(data)
    } else if (type === buttonType.Line) {
      text = toLineText(data)
    }

    try {
      await navigator.clipboard.writeText(text)
      alert("テキストをコピーしました")
    } catch (error) {
      alert("テキストのコピーに失敗しました")
    }
  }

  const disableAutoDetect = autoDetect(
    storeName,
    adPrice,
    isRecipient === "true",
    nop,
    applicatedAt
  )

  function handleStrategyChange() {
    const autoDetermine = autoDetermineFormula({
      adPrice,
      storeName,
      isRecipient: isRecipient === "true",
      nop,
      applicatedAt: dayjs(applicatedAt),
    })
    setValue("strategy", autoDetermine)
  }

  const debouncedUpdateQueryParams = useCallback(
    debounce((values) => {
      updateQueryParams(values)
    }, 500),
    []
  )

  useEffect(() => {
    debouncedUpdateQueryParams(watchedValues)
    // クリーンアップ関数でデバウンスされた関数をキャンセル
    return () => {
      debouncedUpdateQueryParams.cancel()
    }
  }, [watchedValues, debouncedUpdateQueryParams])

  return (
    <form onSubmit={handleSubmit(copyText)}>
      <StyledCalculatorWrapperDiv>
        <header>
          <StyledHeading1>送金額計算書</StyledHeading1>
        </header>
        <ApplicationInformation setValue={setValue} watch={watch} errors={errors} />
        <div className="contents">
          <StyledHeading2>計算式</StyledHeading2>
          <div className="section">
            <StyledCalculationMethodDiv>
              <SelectField
                name="strategy"
                label="計算方式"
                options={strategyOptions}
                value={strategyOptions.find((option) => option.value === strategy)}
                onChange={(e: Option | null) => {
                  if (typeof e.value === "string") {
                    setValue("strategy", e.value)
                  }
                }}
                errorText={errors?.strategy?.message}
                required
                hint="計算方式を選択してください。"
              />
              <StyledAutoDetectButton
                disabled={disableAutoDetect}
                type="button"
                onClick={() => {
                  handleStrategyChange()
                }}
              >
                計算方式を自動判定
              </StyledAutoDetectButton>
              {disableAutoDetect && (
                <p>
                  ※自動判定には加盟店名、AD費用、審査申込日、店舗利用かを入力する必要があります。
                </p>
              )}
            </StyledCalculationMethodDiv>
          </div>
          <Formulas setValue={setValue} watch={watch} errors={errors} strategy={strategy} />
          <StyledCopyButtonWrapper>
            <div className="section">
              <button type="submit" className="copy-button" value={buttonType.SpreadSheet}>
                SpreadSheet用の文をコピー
              </button>
            </div>
            <div className="section">
              <button type="submit" className="copy-button" value={buttonType.Line}>
                LINE用の文をコピー
              </button>
            </div>
          </StyledCopyButtonWrapper>
        </div>
      </StyledCalculatorWrapperDiv>
    </form>
  )
}

const StyledCalculatorWrapperDiv = styled("div")`
  padding: 16px;
  font-size: 14px;

  .contents {
    box-sizing: border-box;
    padding: 0 24px;
  }

  .contents .section {
    margin-bottom: 18px;
    display: flex;
    flex-direction: row;
  }

  .contents .section > * {
    margin-left: 8px;
  }

  .contents .section > *:first-child {
    margin-left: 0px;
  }

  .contents .section > span {
    margin-bottom: 16px;
  }

  .contents .section .field {
    width: 204px;
  }

  .copy-button {
    color: #3f51b5;
    margin-right: 20px;
  }
`

const StyledHeading1 = styled.h1`
  font-size: 32px;
`

const StyledHeading2 = styled.h2`
  font-size: 24px;
  margin-top: 20px;
  margin-bottom: 10px;
`

const StyledCalculationMethodDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`

const StyledAutoDetectButton = styled.button<{ disabled: boolean }>`
  background-color: ${({ disabled }) => (disabled ? "#d3d3d3" : "gray")};
  margin-top: 8px;
  padding: 8px 9px;
  border: none;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  width: 170px;
  height: 50px;
`

const StyledCopyButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin-top: 20px;
`
